import { useCallback } from "react";
import type { FormErrors } from "redux-form";
import _ from "underscore";
import { z } from "zod";

import { fetchCurrentUser } from "@js/apps/auth/actions";
import { openCreateJobThrottlingModal } from "@js/apps/jobs/apps/create-job/views/create-job/create-job-throttling-modal";
import { useIsEmployerImpersonated } from "@js/apps/jobs/hooks";
import { Snackbar } from "@js/components/snackbar";
import { useAppDispatch, useNavigate } from "@js/hooks";
import type { JobFormValues } from "@js/types/jobs";

import { getRequiredFieldsWhenPostingForAClient } from "../../components/can-manage-job-on-behalf-of-client-module/post-job-for-client/helpers";
import { PostJobForClientModalInstance } from "../../components/can-manage-job-on-behalf-of-client-module/post-job-for-client/modal";
import { closeSummarizeJobModal } from "../../components/summarize-module";

export const useHandleFailedSubmission = (draftId: number | undefined) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isEmployerImpersonated = useIsEmployerImpersonated();

  const onSubmitFail = useCallback(
    (errors: Record<string, unknown>) => {
      const errorsKeys = Object.entries(errors)
        // eslint-disable-next-line eqeqeq
        .filter(([_key, error]) => error != null)
        .map(([key]) => key);

      const hasExceededDailyJobPostingLimit =
        getHasExceededDailyJobLimit(errors);

      const hasJobMarkedAsSpam =
        errorsKeys.includes("_error") &&
        _.isArray(errors["_error"]) &&
        errors["_error"].includes("job_marked_as_spam");

      if (hasJobMarkedAsSpam) {
        Snackbar.error(
          "We do periodic checks on new accounts to verify the authenticity of the company posting.",
        );
        navigate("/");
        dispatch(fetchCurrentUser()).catch(() => null);
      }

      if (hasExceededDailyJobPostingLimit) {
        openCreateJobThrottlingModal();
      }

      // post job for a client error can happen only in first step
      const errorInPostJobForClient = getRequiredFieldsWhenPostingForAClient(
        isEmployerImpersonated,
      ).some((key) => errorsKeys.includes(key));
      if (errorInPostJobForClient) {
        PostJobForClientModalInstance.open();
        return;
      }

      if (
        errorsKeys.includes("openings_number") ||
        errorsKeys.includes("other_role_name")
      ) {
        navigate(`/jobs/add_new/${draftId}/set_up/`);
      }
      closeSummarizeJobModal();
    },
    [isEmployerImpersonated, navigate, draftId, dispatch],
  );

  return onSubmitFail;
};

const hasExceededDailyJobPostingLimitSchema = z.object({
  _error: z.coerce.string(),
});

export const getHasExceededDailyJobLimit = (
  errors: FormErrors<JobFormValues, unknown>,
) => {
  const parseResult = hasExceededDailyJobPostingLimitSchema.safeParse(errors);
  if (!parseResult.success) {
    return false;
  }
  const parsedError = parseResult.data._error;
  // Reached maximum limit of pending jobs.
  // Reached maximum limit of jobs to automatically publish. Job is saved as a draft.
  return (
    parsedError.toLowerCase().startsWith("reached maximum limit") ||
    parsedError.includes("daily_job_posting_limit_exceeded")
  );
};
